import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useEngine } from '../api/context';
import { useMetaIndex } from '../meta/useMeta';
import { resourceKeys } from '../api/hooks';
import { labelOf } from '../screens/ResourceFormScreen';

// Resolves the current route to a "record tab" target when it is a detail route (a screen
// whose path carries an `:id`). The label is the record's real display name when the detail
// screen has already loaded it into the query cache, else the screen/resource label with a
// short id — cheap, no extra fetch (the task asks the tabs strip to stay lightweight).

export interface RouteRecord {
  path: string;
  label: string;
  resource: string;
  id: string;
}

export function useRouteRecord(): RouteRecord | null {
  const { pathname } = useLocation();
  const { modules } = useEngine();
  const metaIndex = useMetaIndex();
  const qc = useQueryClient();

  return useMemo(() => {
    const screens = modules.flatMap((m) => m.screens);
    for (const s of screens) {
      if (!s.path.includes('/:id') || !s.resource) continue;
      const re = new RegExp('^' + s.path.replace(/:[^/]+/g, '([^/]+)') + '$');
      const m = pathname.match(re);
      if (!m) continue;
      const id = m[1]!;
      const meta = metaIndex.data?.resources.find((r) => r.name === s.resource);
      // Look for a cached row for this id (any expand variant).
      let row: Record<string, unknown> | undefined;
      const cached = qc.getQueriesData<Record<string, unknown>>({ queryKey: resourceKeys.item(s.resource, id) });
      for (const [, data] of cached) if (data && typeof data === 'object') { row = data; break; }
      const shortId = id.length > 8 ? id.slice(0, 8) : id;
      const label = (row && meta && labelOf(meta, row)) || s.title || meta?.label || 'Record';
      return { path: pathname, label: label === (meta?.label ?? '') ? `${label} · ${shortId}` : label, resource: s.resource, id };
    }
    return null;
  }, [pathname, modules, metaIndex.data, qc]);
}
